const CROSSREF_SCHEMA_VERSION = '5.4.0';
const CROSSREF_NS = `http://www.crossref.org/schema/${CROSSREF_SCHEMA_VERSION}`;
const CROSSREF_SCHEMA_URL = `https://www.crossref.org/schemas/crossref${CROSSREF_SCHEMA_VERSION}.xsd`;
const JATS_NS = 'http://www.ncbi.nlm.nih.gov/JATS1';
const XSI_NS = 'http://www.w3.org/2001/XMLSchema-instance';

export { CROSSREF_NS, CROSSREF_SCHEMA_VERSION, JATS_NS };

/**
 * Creates a new XML Document with a `<doi_batch>` root element
 * in the Crossref 5.4.0 namespace, including standard schema attributes.
 */
export function createCrossrefDocument(): Document {
  const doc = document.implementation.createDocument(
    CROSSREF_NS,
    'doi_batch',
    null
  );

  const root = doc.documentElement;
  root.setAttributeNS('http://www.w3.org/2000/xmlns/', 'xmlns:xsi', XSI_NS);
  root.setAttributeNS(
    XSI_NS,
    'xsi:schemaLocation',
    `${CROSSREF_NS} ${CROSSREF_SCHEMA_URL}`
  );
  root.setAttribute('version', CROSSREF_SCHEMA_VERSION);

  return doc;
}

/**
 * Creates a namespaced child element, optionally sets text content and attributes,
 * appends it to the parent, and returns it.
 */
export function appendElement(
  parent: Element,
  name: string,
  text?: string | null,
  attrs?: Record<string, string>
): Element {
  const doc = parent.ownerDocument!;
  const el = doc.createElementNS(CROSSREF_NS, name);

  if (text != null) {
    el.textContent = text;
  }

  if (attrs) {
    for (const [key, value] of Object.entries(attrs)) {
      el.setAttribute(key, value);
    }
  }

  parent.appendChild(el);
  return el;
}

/**
 * Creates a child element in the JATS namespace, optionally sets text content
 * and attributes, appends it to the parent, and returns it.
 */
export function appendJatsElement(
  parent: Element,
  name: string,
  text?: string | null,
  attrs?: Record<string, string>
): Element {
  const doc = parent.ownerDocument!;
  const el = doc.createElementNS(JATS_NS, `jats:${name}`);

  if (text != null) {
    el.textContent = text;
  }

  if (attrs) {
    for (const [key, value] of Object.entries(attrs)) {
      el.setAttribute(key, value);
    }
  }

  parent.appendChild(el);
  return el;
}

/**
 * Conditionally appends a Crossref-namespaced element only when `text` is truthy.
 * Returns the element if created, or `null` if skipped.
 */
export function appendElementIf(
  parent: Element,
  name: string,
  text?: string | null,
  attrs?: Record<string, string>
): Element | null {
  if (!text) return null;
  return appendElement(parent, name, text, attrs);
}

/**
 * Serializes an XML Document to a string, prepending the XML declaration.
 */
export function serializeXml(doc: Document, prettyPrint = false): string {
  const serializer = new XMLSerializer();
  const xmlString = serializer.serializeToString(doc);
  const declaration = '<?xml version="1.0" encoding="UTF-8"?>';

  if (!prettyPrint) {
    return `${declaration}\n${xmlString}`;
  }

  return `${declaration}\n${formatXml(xmlString)}`;
}

/**
 * Naive XML pretty-printer: inserts newlines and indentation.
 * Handles self-closing tags, opening tags, closing tags, and text nodes.
 */
function formatXml(xml: string): string {
  let formatted = '';
  let indent = 0;
  const parts = xml.replace(/(>)\s*(<)/g, '$1\n$2').split('\n');

  for (const part of parts) {
    const trimmed = part.trim();
    if (!trimmed) continue;

    if (trimmed.startsWith('</')) {
      indent = Math.max(indent - 1, 0);
    }

    formatted += '  '.repeat(indent) + trimmed + '\n';

    if (
      !trimmed.startsWith('</') &&
      !trimmed.startsWith('<?') &&
      !trimmed.endsWith('/>') &&
      // Don't indent after elements that contain text content inline
      !/<[^/][^>]*>[^<]+<\//.test(trimmed)
    ) {
      indent++;
    }
  }

  return formatted.trimEnd();
}
